/**
 * Folding the jurisdiction answers into the EXISTING exam-schedule step (#411).
 *
 * The learner already told us where they are in the board process; asking "when
 * are you testing?" from a blank slate right afterwards is the same question
 * twice. So the licensing answer PRE-FILLS the ExamScheduler status, and in the
 * two cases where the exam is not a thing to schedule at all the screen is
 * skipped outright:
 *
 *   - `passed`: there is nothing left to schedule.
 *   - the `limited` rung (Michigan's Limited LMSW): the exam is thousands of
 *     supervised hours away. Asking for an exam date here is exactly the
 *     "buy an ASWB seat two years early" mistake the feature exists to prevent.
 *
 * Everything else keeps today's screen, just started on the right status.
 */
import type { ExamScheduleEntry, ExamStatus } from '@/models/UserTypes';
import type { LicensingAnswers, ProcessStage } from './types';

const STATUS_FROM_STAGE: Record<ProcessStage, ExamStatus> = {
  not_applied: 'exploring',
  waiting: 'waiting',
  approved: 'approved',
  scheduled: 'scheduled',
  // A retaker has a date to pick again, but nothing is approved yet as far as we know.
  retaking: 'exploring',
  passed: 'exploring',
};

export interface FoldedExamSchedule {
  /** The status the ExamScheduler should start on, or null when nothing is implied. */
  status: ExamStatus | null;
  /** True when the exam-date screen has nothing left to ask this learner. */
  skipExamScreen: boolean;
  /** The entry to record when the screen is skipped. */
  entry: ExamScheduleEntry | null;
}

export function foldExamSchedule(answers: LicensingAnswers): FoldedExamSchedule {
  const { processStage, licenseRung } = answers;

  if (licenseRung === 'limited') {
    return { status: 'accruing', skipExamScreen: true, entry: { status: 'accruing', updatedAt: '' } };
  }
  if (processStage === 'passed') {
    return { status: 'exploring', skipExamScreen: true, entry: { status: 'exploring', updatedAt: '' } };
  }
  if (!processStage) return { status: null, skipExamScreen: false, entry: null };
  return { status: STATUS_FROM_STAGE[processStage], skipExamScreen: false, entry: null };
}
